import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Lock, Mail, ArrowRight, ShieldCheck, AlertCircle } from 'lucide-react';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { signIn, isConfigured } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    setIsLoading(true);

    const { error } = await signIn(email, password);
    setIsLoading(false);

    if (error) {
      setErrorMsg(error.message);
    } else {
      navigate('/');
    }
  };

  const handleDemoAccess = () => {
    navigate('/');
  };
  return (

    <div
      className="
        relative
        min-h-[100dvh]
        overflow-hidden
        bg-[#020817]
        text-slate-100
        flex
        items-center
        justify-center
        px-4
        py-5
        sm:p-6
      "
    >

      <style>
        {`
          @keyframes kaizenAuthFloat {
            0%, 100% {
              transform: translate3d(0,0,0) scale(1);
              opacity: .55;
            }

            50% {
              transform: translate3d(0,-14px,0) scale(1.05);
              opacity: .95;
            }
          }

          @keyframes kaizenAuthScan {
            0% {
              transform: translateY(-120px);
              opacity: 0;
            }

            10% {
              opacity: .55;
            }

            85% {
              opacity: .18;
            }

            100% {
              transform: translateY(520px);
              opacity: 0;
            }
          }

          @keyframes kaizenAuthSweep {
            0% {
              transform: translateX(-180%) rotate(12deg);
            }

            55%, 100% {
              transform: translateX(280%) rotate(12deg);
            }
          }

          @keyframes kaizenAuthBars {
            0%, 100% {
              transform: scaleY(.35);
              opacity: .25;
            }

            50% {
              transform: scaleY(1);
              opacity: 1;
            }
          }

          @keyframes kaizenAuthGrid {
            from {
              transform: translateY(0);
            }

            to {
              transform: translateY(36px);
            }
          }

          @media (prefers-reduced-motion: reduce) {
            .kaizen-auth-motion {
              animation: none !important;
            }
          }
        `}
      </style>


      <div
        className="
          pointer-events-none
          absolute
          inset-0
          opacity-[0.36]
        "
        style={{
          backgroundImage:
            'linear-gradient(rgba(99,102,241,0.055) 1px, transparent 1px), linear-gradient(90deg, rgba(99,102,241,0.055) 1px, transparent 1px)',

          backgroundSize:
            '36px 36px'
        }}
      />


      <div
        className="
          kaizen-auth-motion
          pointer-events-none
          absolute
          inset-x-0
          top-[-36px]
          h-[calc(100%+36px)]
          opacity-25
        "
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px)',

          backgroundSize:
            '100% 36px',

          animation:
            'kaizenAuthGrid 14s linear infinite'
        }}
      />


      <div
        className="
          kaizen-auth-motion
          pointer-events-none
          absolute
          -top-40
          -left-32
          w-[440px]
          h-[440px]
          rounded-full
          bg-indigo-500/[0.14]
          blur-[115px]
        "
        style={{
          animation:
            'kaizenAuthFloat 8s ease-in-out infinite'
        }}
      />


      <div
        className="
          kaizen-auth-motion
          pointer-events-none
          absolute
          -bottom-44
          -right-32
          w-[480px]
          h-[480px]
          rounded-full
          bg-cyan-400/[0.06]
          blur-[130px]
        "
        style={{
          animation:
            'kaizenAuthFloat 11s ease-in-out infinite reverse'
        }}
      />


      <div
        className="
          pointer-events-none
          absolute
          inset-x-0
          top-0
          h-px
          bg-gradient-to-r
          from-transparent
          via-indigo-300/50
          to-transparent
        "
      />


      <main
        className="
          relative
          z-10
          w-full
          max-w-[470px]
        "
      >

        <section
          className="
            relative
            overflow-hidden
            rounded-[18px]
            border
            border-white/[0.09]
            bg-slate-950/65
            backdrop-blur-2xl
            shadow-[0_32px_100px_rgba(0,0,0,0.40)]
          "
        >

          <div
            className="
              pointer-events-none
              absolute
              inset-x-0
              top-0
              h-px
              bg-gradient-to-r
              from-transparent
              via-indigo-300/65
              to-transparent
            "
          />


          <div
            className="
              pointer-events-none
              absolute
              -top-20
              right-[-70px]
              w-56
              h-56
              rounded-full
              bg-indigo-500/[0.10]
              blur-3xl
            "
          />


          <div
            className="
              relative
              px-5
              pt-5
              pb-4
              sm:px-6
              sm:pt-6
            "
          >

            <div
              className="
                flex
                items-center
                justify-between
                gap-3
              "
            >

              <div
                className="
                  flex
                  items-center
                  gap-3
                  min-w-0
                "
              >

                <div
                  className="
                    relative
                    w-12
                    h-12
                    shrink-0
                    rounded-xl
                    border
                    border-indigo-400/25
                    bg-indigo-400/[0.08]
                    text-indigo-300
                    flex
                    items-center
                    justify-center
                    overflow-hidden
                    shadow-[0_0_36px_rgba(99,102,241,0.09)]
                  "
                >

                  <span
                    className="
                      relative
                      z-10
                      text-lg
                      font-semibold
                    "
                  >
                    K
                  </span>


                  <div
                    className="
                      kaizen-auth-motion
                      pointer-events-none
                      absolute
                      inset-x-0
                      h-5
                      bg-gradient-to-b
                      from-transparent
                      via-indigo-300/25
                      to-transparent
                    "
                    style={{
                      animation:
                        'kaizenAuthScan 4.6s linear infinite'
                    }}
                  />

                </div>


                <div
                  className="
                    min-w-0
                  "
                >

                  <div
                    className="
                      text-[13px]
                      font-semibold
                      text-slate-100
                      truncate
                    "
                  >
                    KAIZEN Remote
                  </div>


                  <div
                    className="
                      mt-1
                      font-mono
                      text-[7px]
                      sm:text-[8px]
                      tracking-[0.16em]
                      text-slate-600
                    "
                  >
                    SECURE ACCESS GATEWAY
                  </div>

                </div>

              </div>


              <div
                className="
                  inline-flex
                  items-center
                  gap-1.5
                  rounded-md
                  border
                  border-emerald-400/15
                  bg-emerald-400/[0.055]
                  px-2
                  py-1
                  font-mono
                  text-[7px]
                  sm:text-[8px]
                  text-emerald-300
                "
              >

                <span
                  className="
                    relative
                    flex
                    w-1.5
                    h-1.5
                  "
                >

                  <span
                    className="
                      kaizen-auth-motion
                      absolute
                      inline-flex
                      h-full
                      w-full
                      rounded-full
                      bg-emerald-400
                      opacity-45
                      animate-ping
                    "
                  />


                  <span
                    className="
                      relative
                      inline-flex
                      w-1.5
                      h-1.5
                      rounded-full
                      bg-emerald-400
                    "
                  />

                </span>

                SECURE

              </div>

            </div>


            <div
              className="
                mt-7
              "
            >

              <div
                className="
                  inline-flex
                  items-center
                  gap-2
                  rounded-md
                  border
                  border-indigo-400/15
                  bg-indigo-400/[0.055]
                  px-2.5
                  py-1.5
                "
              >

                <ShieldCheck
                  className="
                    w-3
                    h-3
                    text-indigo-300
                  "
                />


                <span
                  className="
                    text-[7px]
                    sm:text-[8px]
                    font-mono
                    uppercase
                    tracking-[0.12em]
                    text-indigo-300
                  "
                >
                  Authenticated Control Plane
                </span>

              </div>


              <h1
                className="
                  mt-4
                  text-[27px]
                  sm:text-[30px]
                  leading-[1.08]
                  tracking-[-0.035em]
                  font-semibold
                  text-slate-100
                "
              >
                Enter your
                <span
                  className="
                    text-indigo-300
                  "
                >
                  {' '}workspace.
                </span>
              </h1>


              <p
                className="
                  mt-2.5
                  max-w-sm
                  text-[10px]
                  sm:text-[11px]
                  leading-relaxed
                  text-slate-500
                "
              >
                Establish an authenticated session with the
                KAIZEN Remote control plane.
              </p>

            </div>


            <form
              onSubmit={
                handleSubmit
              }
              className="
                mt-6
                space-y-4
              "
            >

              <div>

                <label
                  htmlFor="kaizen-email"
                  className="
                    block
                    mb-2
                    text-[8px]
                    uppercase
                    tracking-[0.12em]
                    font-medium
                    text-slate-500
                  "
                >
                  Account
                </label>


                <div
                  className="
                    relative
                    group
                  "
                >

                  <Mail
                    className="
                      absolute
                      left-3.5
                      top-1/2
                      -translate-y-1/2
                      w-4
                      h-4
                      text-slate-600
                      group-focus-within:text-indigo-300
                      transition-colors
                    "
                  />


                  <input
                    id="kaizen-email"
                    type="email"
                    value={
                      email
                    }
                    onChange={
                      event =>
                        setEmail(
                          event.target.value
                        )
                    }
                    required
                    autoComplete="email"
                    placeholder="Account email"
                    className="
                      w-full
                      h-[50px]
                      rounded-xl
                      border
                      border-white/[0.08]
                      bg-white/[0.025]
                      pl-11
                      pr-4
                      text-[11px]
                      sm:text-xs
                      text-slate-200
                      placeholder:text-slate-700
                      outline-none
                      transition-all
                      focus:border-indigo-400/40
                      focus:bg-white/[0.04]
                      focus:shadow-[0_0_0_3px_rgba(99,102,241,0.07),0_0_30px_rgba(99,102,241,0.05)]
                    "
                  />

                </div>

              </div>


              <div>

                <div
                  className="
                    mb-2
                    flex
                    items-center
                    justify-between
                  "
                >

                  <label
                    htmlFor="kaizen-password"
                    className="
                      text-[8px]
                      uppercase
                      tracking-[0.12em]
                      font-medium
                      text-slate-500
                    "
                  >
                    Password
                  </label>


                  <span
                    className="
                      font-mono
                      text-[7px]
                      tracking-[0.10em]
                      text-slate-700
                    "
                  >
                    ENCRYPTED
                  </span>

                </div>


                <div
                  className="
                    relative
                    group
                  "
                >

                  <Lock
                    className="
                      absolute
                      left-3.5
                      top-1/2
                      -translate-y-1/2
                      w-4
                      h-4
                      text-slate-600
                      group-focus-within:text-indigo-300
                      transition-colors
                    "
                  />


                  <input
                    id="kaizen-password"
                    type="password"
                    value={
                      password
                    }
                    onChange={
                      event =>
                        setPassword(
                          event.target.value
                        )
                    }
                    required
                    autoComplete="current-password"
                    placeholder="Password"
                    className="
                      w-full
                      h-[50px]
                      rounded-xl
                      border
                      border-white/[0.08]
                      bg-white/[0.025]
                      pl-11
                      pr-4
                      text-[11px]
                      sm:text-xs
                      text-slate-200
                      placeholder:text-slate-700
                      outline-none
                      transition-all
                      focus:border-indigo-400/40
                      focus:bg-white/[0.04]
                      focus:shadow-[0_0_0_3px_rgba(99,102,241,0.07),0_0_30px_rgba(99,102,241,0.05)]
                    "
                  />

                </div>

              </div>


              {errorMsg && (

                <div
                  className="
                    rounded-xl
                    border
                    border-rose-400/15
                    bg-rose-400/[0.06]
                    px-3.5
                    py-3
                    flex
                    items-start
                    gap-2.5
                  "
                >

                  <AlertCircle
                    className="
                      mt-0.5
                      w-3.5
                      h-3.5
                      shrink-0
                      text-rose-300
                    "
                  />


                  <span
                    className="
                      text-[9px]
                      leading-relaxed
                      text-rose-300
                    "
                  >
                    {errorMsg}
                  </span>

                </div>

              )}


              <button
                type="submit"
                disabled={
                  isLoading
                }
                className="
                  relative
                  overflow-hidden
                  w-full
                  h-[50px]
                  mt-1
                  rounded-xl
                  border
                  border-indigo-300/20
                  bg-indigo-600
                  hover:bg-indigo-500
                  active:scale-[0.99]
                  disabled:opacity-55
                  disabled:cursor-not-allowed
                  text-[11px]
                  sm:text-xs
                  font-semibold
                  text-white
                  shadow-[0_17px_46px_rgba(79,70,229,0.24)]
                  transition-all
                  flex
                  items-center
                  justify-center
                  gap-2
                "
              >

                <span
                  className="
                    kaizen-auth-motion
                    pointer-events-none
                    absolute
                    inset-y-[-30px]
                    w-24
                    bg-gradient-to-r
                    from-transparent
                    via-white/15
                    to-transparent
                  "
                  style={{
                    animation:
                      'kaizenAuthSweep 4.5s ease-in-out infinite'
                  }}
                />


                <span
                  className="
                    relative
                    z-10
                  "
                >
                  {isLoading
                    ? 'Establishing Session…'
                    : 'Enter KAIZEN'}
                </span>


                {!isLoading && (

                  <ArrowRight
                    className="
                      relative
                      z-10
                      w-4
                      h-4
                    "
                  />

                )}

              </button>

            </form>


            <div
              className="
                mt-6
                pt-4
                border-t
                border-white/[0.06]
              "
            >

              <div
                className="
                  grid
                  grid-cols-3
                  gap-2
                "
              >

                {[
                  'TLS',
                  'AUTH',
                  'HOST'
                ].map(
                  (
                    label,
                    index
                  ) => (

                    <div
                      key={
                        label
                      }
                      className="
                        relative
                        overflow-hidden
                        min-h-[42px]
                        rounded-lg
                        border
                        border-white/[0.055]
                        bg-white/[0.018]
                        flex
                        items-center
                        justify-center
                        gap-1.5
                      "
                    >

                      <span
                        className="
                          relative
                          flex
                          w-1.5
                          h-1.5
                        "
                      >

                        <span
                          className="
                            kaizen-auth-motion
                            absolute
                            inline-flex
                            h-full
                            w-full
                            rounded-full
                            bg-emerald-400
                            opacity-40
                            animate-ping
                          "
                          style={{
                            animationDelay:
                              `${index * 240}ms`
                          }}
                        />


                        <span
                          className="
                            relative
                            inline-flex
                            w-1.5
                            h-1.5
                            rounded-full
                            bg-emerald-400
                          "
                        />

                      </span>


                      <span
                        className="
                          font-mono
                          text-[7px]
                          tracking-[0.11em]
                          text-slate-600
                        "
                      >
                        {label}
                      </span>

                    </div>

                  )
                )}

              </div>


              <div
                className="
                  mt-4
                  flex
                  items-end
                  justify-center
                  gap-[3px]
                  h-4
                "
                aria-hidden="true"
              >

                {[
                  5,
                  9,
                  7,
                  13,
                  8,
                  15,
                  6,
                  11,
                  8,
                  14,
                  6,
                  10,
                  7,
                  12
                ].map(
                  (
                    height,
                    index
                  ) => (

                    <span
                      key={
                        `${height}-${index}`
                      }
                      className="
                        kaizen-auth-motion
                        block
                        w-[3px]
                        origin-bottom
                        rounded-full
                        bg-indigo-400/45
                      "
                      style={{
                        height:
                          `${height}px`,

                        animation:
                          `kaizenAuthBars ${
                            1.15 +
                            (
                              index %
                              4
                            ) *
                            .18
                          }s ease-in-out infinite`,

                        animationDelay:
                          `${index * 85}ms`
                      }}
                    />

                  )
                )}

              </div>


              <div
                className="
                  mt-3
                  text-center
                  font-mono
                  text-[7px]
                  leading-relaxed
                  uppercase
                  tracking-[0.11em]
                  text-slate-700
                "
              >
                Encrypted outbound control · No exposed host ports
              </div>

            </div>

          </div>

        </section>


        <div
          className="
            mt-3
            flex
            items-center
            justify-center
            gap-2
            font-mono
            text-[7px]
            uppercase
            tracking-[0.12em]
            text-slate-800
          "
        >

          <span
            className="
              w-1
              h-1
              rounded-full
              bg-indigo-500/50
            "
          />

          KAIZEN Remote Control Plane

          <span
            className="
              w-1
              h-1
              rounded-full
              bg-indigo-500/50
            "
          />

        </div>

      </main>

    </div>
  );
};